import { formatBinaryFileSize } from '@/utils/format-binary-file-size';
import type { SpaceNodePayload } from '@/spaces/types/types';

/**
 * Maps Ask Pendo artifact types → space canvas payloads (`kind` must match `nodeTypes` in
 * `space-content.vue` unless you add a new node component).
 *
 * To wire a new artifact: add a `case` here, then in the artifact SFC call
 * `await this.appendAddToSpaceMenuItem(items, { spacesFlag, artifactType, context })` when building
 * the menu (`context()` should return `{ artifactId, name, data, conversationId?, messageId? }`; guides may add `documentMarkdown`).
 */
export type AskPendoArtifactSpaceBuildCtx = {
    artifactId: string;
    name: string;
    data: unknown;
    /** Ask Pendo conversation id; persisted on the space item `data` when set. */
    conversationId?: string | null;
    /** Thread message id containing the artifact (for reopen + scroll). */
    messageId?: string | null;
    /** For `guide` — pre-built markdown (include translated copy). */
    documentMarkdown?: string;
};

/** Persisted on space item `data` for Ask Leo reopen + `scrollToMessage`. */
export function askPendoArtifactProvenanceFields (
    conversationId?: string | null,
    messageId?: string | null,
): { conversationId?: string; messageId?: string } {
    const fields: { conversationId?: string; messageId?: string } = {};
    if (typeof conversationId === 'string' && conversationId.trim().length > 0) {
        fields.conversationId = conversationId.trim();
    }
    if (typeof messageId === 'string' && messageId.trim().length > 0) {
        fields.messageId = messageId.trim();
    }

    return fields;
}

/** Fields sent to `addNodeToBacklogOfSpace` aside from `isBacklog` / `isLive`. */
export type AskPendoSpaceNodeSpec = Pick<
    SpaceNodePayload,
    'name' | 'kind' | 'data' | 'height' | 'width' | 'type'
>;

const NODE_DEFAULT_SIZES: Record<string, { height: number; width: number }> = {
    document: { height: 400, width: 500 },
    table: { height: 600, width: 800 },
    markdown_table: { height: 400, width: 700 },
    todo_list: { height: 320, width: 480 },
    custom_highcharts: { height: 420, width: 640 },
    session_files: { height: 400, width: 560 },
    guide: { height: 360, width: 520 },
    segment_definition: { height: 400, width: 700 },
};

/**
 * Renders a todo list as GitHub-style task markdown for a space document node.
 *
 * @param todos - Task rows with optional `task` label and `status` (`completed` → checked)
 * @returns Markdown body; `_No tasks._` when the list is empty
 */
function todoTasksToMarkdown (
    todos: Array<{ task?: string; status?: string }>,
): string {
    if (!todos.length) {
        return '_No tasks._';
    }

    return todos
        .map((t) => {
            const checked = t.status === 'completed' ? 'x' : ' ';

            return `- [${checked}] ${t.task ?? ''}`;
        })
        .join('\n');
}

/**
 * Builds markdown for a session-files artifact: heading plus bullet list with optional formatted sizes.
 *
 * @param data - Session payload containing `files` entries
 * @returns Markdown body; placeholder line when no files
 */
function sessionFilesToMarkdown (data: {
    files?: Array<{ name: string; path?: string; size?: number }>;
}): string {
    const files = data?.files ?? [];
    if (!files.length) {
        return '_No files were created during this session._';
    }

    const lines = files.map((file) => {
        const size = formatBinaryFileSize(file.size);

        return size ? `- **${file.name}** (${size})` : `- **${file.name}**`;
    });

    return ['## Session files', '', ...lines].join('\n');
}

/**
 * Maps an Ask Pendo artifact type to a space node spec for the canvas
 * (`document`, `table`, `custom_highcharts`, etc.).
 *
 * @param artifactType - Ask Pendo artifact discriminator (e.g. `document`, `session_files`)
 * @param ctx - Stable id, display name, raw artifact `data`, and optional guide markdown
 * @returns Node fields for `addNodeToBacklogOfSpace`, or `null` when the type is unsupported
 */
export function buildAskPendoArtifactSpacePayload (
    artifactType: string,
    ctx: AskPendoArtifactSpaceBuildCtx,
): AskPendoSpaceNodeSpec | null {
    const { artifactId, name, data, documentMarkdown, conversationId, messageId } = ctx;
    const provenance = askPendoArtifactProvenanceFields(conversationId, messageId);

    switch (artifactType) {
        case 'document': {
            const docData = data as { content?: string } | null;

            return {
                type: 'artifact',
                name,
                kind: 'document',
                data: {
                    content: docData?.content ?? '',
                    artifactId,
                    ...provenance,
                },
                ...NODE_DEFAULT_SIZES.document,
            };
        }
        case 'table': {
            const tableData = data as { columns?: unknown[]; rows?: unknown[] } | null;

            return {
                type: 'artifact',
                name,
                kind: 'table',
                data: {
                    tableColumns: tableData?.columns ?? [],
                    tableData: tableData?.rows ?? [],
                    artifactId,
                    ...provenance,
                },
                ...NODE_DEFAULT_SIZES.table,
            };
        }
        case 'markdown_table':
            return {
                type: 'artifact',
                name,
                kind: 'document',
                data: {
                    content: typeof data === 'string' ? data : '',
                    artifactId,
                    ...provenance,
                },
                ...NODE_DEFAULT_SIZES.markdown_table,
            };
        case 'todo_list':
            return {
                type: 'artifact',
                name,
                kind: 'document',
                data: {
                    content: todoTasksToMarkdown(Array.isArray(data) ? data : []),
                    artifactId,
                    ...provenance,
                },
                ...NODE_DEFAULT_SIZES.todo_list,
            };
        case 'custom_highcharts':
            return {
                type: 'artifact',
                name,
                kind: 'custom_highcharts',
                data: {
                    chartConfig: (data ?? {}) as Record<string, unknown>,
                    artifactId,
                    ...provenance,
                },
                ...NODE_DEFAULT_SIZES.custom_highcharts,
            };
        case 'session_files':
            return {
                type: 'artifact',
                name,
                kind: 'document',
                data: {
                    content: sessionFilesToMarkdown(
                        (data ?? {}) as { files?: Array<{ name: string; path?: string; size?: number }> },
                    ),
                    artifactId,
                    ...provenance,
                },
                ...NODE_DEFAULT_SIZES.session_files,
            };
        case 'segment_definition': {
            const segmentPayload =
                data != null && typeof data === 'object' && !Array.isArray(data)
                    ? { ...(data as Record<string, unknown>) }
                    : {};

            return {
                type: 'artifact',
                name,
                kind: 'segment_definition',
                data: {
                    ...segmentPayload,
                    ...provenance,
                    artifactId,
                },
                ...NODE_DEFAULT_SIZES.segment_definition,
            };
        }
        case 'guide':
            return {
                type: 'artifact',
                name,
                kind: 'document',
                data: {
                    content:
                        documentMarkdown
                        ?? `## ${name}\n\n_Guide artifact — open Ask Pendo to preview or edit steps._`,
                    artifactId,
                    ...provenance,
                },
                ...NODE_DEFAULT_SIZES.guide,
            };
        default:
            return null;
    }
}
